import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type {
  AoSGameState,
  BuildTrackArgs,
  TrackTile,
  GoodsColor,
  BuildTrackHistoryEntry
} from '../../types';
import {
  coordToKey
} from '../../types';
import { validateBuildTrack } from '../validators/buildTrackValidator';
import { detectNewCompletedLinks } from '../utils/linkCalculator';
import { getTileDefinition } from '../constants/tiles';
import type { TrackOwnership } from '../../types';
import {
  updateUnfinishedSections,
  removeSectionsForCompletedLink,
  clearSectionsAtUrbanizedTown
} from '../utils/unfinishedSectionManager';
import { updateBuildCostInfo } from './passBuildTrack';
import { calculateBuildCost } from '../utils/buildCostCalculator';

/**
 * 線路建設Move（基本ルール）
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param args - 線路建設引数
 * @returns void or INVALID_MOVE
 *
 * @see docs/specifications/logic/common/phase_04_build_track.md
 */
export function buildTrack(
  { G, ctx }: { G: AoSGameState; ctx: Ctx },
  args: BuildTrackArgs
): void | typeof INVALID_MOVE {
  // 1. バリデーション
  const validation = validateBuildTrack(G, ctx, args);
  if (!validation.success) {
    console.error(`[buildTrack] Validation failed: ${validation.error} - ${validation.message}`);
    return INVALID_MOVE;
  }

  const player = G.players[ctx.currentPlayer];
  const hexKey = coordToKey(args.hexCoordinate);
  const hex = G.map.find(h => coordToKey(h.coordinate) === hexKey);

  if (!hex) {
    console.error(`[buildTrack] Hex not found: ${hexKey}`);
    return INVALID_MOVE;
  }

  // タイル定義を取得
  const tileDef = getTileDefinition(args.tileType);
  const isNewCityTile = tileDef?.isNewCity === true;

  // 2. コスト計算（新都市タイルは無料）
  const cost = isNewCityTile ? 0 : calculateBuildCost(hex, args.placementType, args.tileType);

  // Issue #15: 置き換え時の元のタイル情報を保存
  const existingTile = hex.tile;
  const existingTileOwner = existingTile?.owner;

  // エンジニアボーナス（最安タイル無料）がある場合、個別タイルの所持金チェックをスキップ
  // ディスカウント後の支払い総額は passBuildTrack で一括チェック
  // UI側は buildCostInfo.isOverBudget で Finish ボタンを無効化する
  const skipPerTileCashCheck = G.mapConfig?.hasEngineerBonus && player.selectedAction === 'ENGINEER';
  if (!skipPerTileCashCheck && player.cash < cost) {
    console.error(`[buildTrack] Insufficient funds: ${player.cash} < ${cost}`);
    return INVALID_MOVE;
  }

  // 履歴用: 元のタイル情報を保存（置き換え時）
  const previousTile = hex.tile ? { ...hex.tile } : null;

  // 履歴用: 町の元の状態を保存（新都市タイル配置時）
  const previousTownState = (isNewCityTile && hex.town) ? {
    isUrbanized: hex.town.isUrbanized,
    newCity: hex.town.newCity ? { ...hex.town.newCity } : null,
  } : null;

  // Issue #3-6: 新都市タイル配置時、既存タイルがあればサプライに戻す
  if (isNewCityTile && hex.tile !== null) {
    debug.log(`[buildTrack] Returning existing tile ${hex.tile.type} to supply`);
    hex.tile = null; // 既存タイルを削除（サプライに戻る）
  }

  // 新都市タイル（都市化）配置時、この Town に Town Disk が配置されていたら返却
  if (isNewCityTile && hex.town && G.townDiskPlaced && G.townDiskPlaced[hex.town.id]) {
    const townId = hex.town.id;
    G.townDiskPlaced[townId] = false;
    if (G.townDisksRemaining !== undefined) {
      G.townDisksRemaining += 1;
      debug.log(`[buildTrack] Town Disk returned from urbanized town ${townId}. Remaining: ${G.townDisksRemaining}`);
    }
  }

  // 3. Tile配置
  // Issue #2-1 修正: tiles.ts の定義から connections を取得
  const connections = calculateConnectionsFromTileType(args.tileType, args.rotation);

  // Issue #15: 置き換え時の所有権管理
  // 注意: existingTileOwner が '' (空文字) の場合も置き換え処理を行う（敷設済み線路の場合）
  let owners: TrackOwnership[] | undefined = undefined;
  if (args.placementType === 'replace' && existingTile && existingTileOwner !== undefined) {
    // 複線タイルの所有権を設定
    // 元の経路（connectionIndex=0）は元のオーナー、新しい経路（connectionIndex=1）は現在のプレイヤー
    const existingTileDef = getTileDefinition(existingTile.type);
    const newTileDef = getTileDefinition(args.tileType);

    if (existingTileDef && newTileDef) {
      // 元のタイルの接続（回転適用済み）
      const existingConnections = existingTileDef.connections.map(pair =>
        pair.map(edge => (edge + existingTile.rotation) % 6).sort((a, b) => a - b)
      );
      const existingPath = existingConnections[0];

      // 新しいタイルの接続（回転適用済み）
      const newConnections = newTileDef.connections.map(pair =>
        pair.map(edge => (edge + args.rotation) % 6).sort((a, b) => a - b)
      );

      owners = [];
      for (let i = 0; i < newConnections.length; i++) {
        const newPath = newConnections[i];
        // 元の経路と一致するかチェック
        const isOriginalPath = newPath.length === existingPath.length &&
          newPath.every((edge, j) => edge === existingPath[j]);

        owners.push({
          playerId: isOriginalPath ? existingTileOwner : ctx.currentPlayer,
          connectionIndex: i
        });
      }

      debug.log(`[buildTrack] Track replacement: original owner='${existingTileOwner}', new line owner=${ctx.currentPlayer}`);
    }
  }

  const tile: TrackTile = {
    id: generateTileId(),
    type: args.tileType,
    coordinate: args.hexCoordinate,
    connections: connections as any, // [[Direction, Direction]] 形式
    rotation: args.rotation,
    owner: ctx.currentPlayer,
    hasTownDisk: hex.town !== null,
    placedTurn: 1, // TODO: G.currentTurn から取得
    owners: owners
  };

  debug.log(`[buildTrack] Tile connections: ${JSON.stringify(connections)}`);

  hex.tile = tile;
  // コスト支払いは passBuildTrack で一括払い（エンジニアボーナス対応）
  // player.cash -= cost;

  // 新都市タイル配置時の特別処理
  // 注: tileDef は上部で既に取得済み
  if (tileDef && tileDef.isNewCity && tileDef.newCityColor && hex.town) {
    // hex.town を更新して新都市を作成
    const newCityId = tileDef.id.replace('NEW_CITY_', ''); // "NEW_CITY_A" → "A"
    const newCityColor = tileDef.newCityColor as GoodsColor;

    // 新都市タイルのIDから対応するダイス番号を取得
    // A→1, B→2, C→3, D→4
    const diceNumberMap: Record<string, number> = {
      'A': 1,
      'B': 2,
      'C': 3,
      'D': 4,
      'E': 5,
      'F': 6,
      'G': 7,
      'H': 8
    };
    const diceNumber = diceNumberMap[newCityId] || 0;

    hex.town.isUrbanized = true;
    hex.town.newCity = {
      id: `NEW_CITY_${newCityId}`,
      name: `New City ${newCityId}`,
      color: newCityColor,
      coordinate: hex.coordinate,
      diceNumber: diceNumber,
      isNewCity: true,
      newCityId: newCityId
    };

    debug.log(`[buildTrack] New city tile placed: ${hex.town.newCity.name} (${newCityColor}) at ${hexKey}, dice: ${diceNumber}`);

    // Issue #9: 既存のリンクのIDを新都市タイルIDに更新
    const oldTownId = hex.town.id;
    const updatedCityId = hex.town.newCity.id;

    for (const link of G.completedLinks) {
      if (link.startId === oldTownId) {
        debug.log(`[buildTrack] Updating link startId: ${oldTownId} → ${updatedCityId}`);
        link.startId = updatedCityId;
      }
      if (link.endId === oldTownId) {
        debug.log(`[buildTrack] Updating link endId: ${oldTownId} → ${updatedCityId}`);
        link.endId = updatedCityId;
      }
    }
  }

  debug.log(`[buildTrack] Player ${ctx.currentPlayer} placed ${args.tileType} tile at ${hexKey}, cost: $${cost}`);

  // 4. リンク完成判定（Issue #12 修正: 複数リンク対応）
  const links = detectNewCompletedLinks(G, args.hexCoordinate, ctx.currentPlayer);

  if (links.length > 0) {
    for (const link of links) {
      G.completedLinks.push(link);
      debug.log(`[buildTrack] Link completed: ${link.startId} → ${link.endId}`);
    }

    // Issue #25: 完成したリンクに含まれるタイルの UnfinishedSections を削除
    // 複線タイルの処理は removeSectionsForCompletedLink 内で行う
    for (const link of links) {
      removeSectionsForCompletedLink(G, link.path);
    }
  }

  // 5. 未完成区間管理
  // Issue #25: 新しい UnfinishedSection 構造に対応
  // Issue #12-4: 新都市タイルは所有者不在のため UnfinishedSections に含めない
  if (!isNewCityTile) {
    // Issue #25: 方向転換（Replace パターン2,3）の判定
    // 方向転換は線路追加ではないため tilesAddedSinceLastTurn を加算しない
    let isDirectionChange = false;
    if (args.placementType === 'replace' && existingTile) {
      const existingTileDef = getTileDefinition(existingTile.type);
      const newTileDef = getTileDefinition(args.tileType);
      if (existingTileDef && newTileDef) {
        const existingConnCount = existingTileDef.connections.length;
        const newConnCount = newTileDef.connections.length;
        // Pattern 2: 単線→単線, Pattern 3: 複線→複線 は方向転換
        // Pattern 1: 単線→複線 は方向転換ではない（新たに線路を追加している）
        if ((existingConnCount === 1 && newConnCount === 1) ||
            (existingConnCount >= 2 && newConnCount >= 2)) {
          isDirectionChange = true;
          debug.log(`[buildTrack] Direction change detected (${existingConnCount} conn → ${newConnCount} conn)`);
        }
      }
    }

    // タイル配置による開放端の更新
    updateUnfinishedSections(G, args.hexCoordinate, tile, ctx.currentPlayer, isDirectionChange);
    debug.log(`[buildTrack] UnfinishedSections updated, total: ${G.unfinishedSections.length}`);
  } else {
    // Issue #33: 新都市タイル配置時は、その座標の既存 UnfinishedSections を削除
    // 都市は終端点なので開放端にはならない
    clearSectionsAtUrbanizedTown(G, args.hexCoordinate);
    debug.log(`[buildTrack] New city tile - cleared unfinished sections at urbanized Town`);
  }

  // 6. プレイヤー状態更新
  // 新都市タイルは敷設数に含めない（別枠で1枚配置可能）
  if (isNewCityTile) {
    // Issue #3-3: 新都市タイルは別カウント
    player.newCityTilesPlacedThisTurn = (player.newCityTilesPlacedThisTurn ?? 0) + 1;
    debug.log(`[buildTrack] New city tile placed: ${player.newCityTilesPlacedThisTurn} (does not count toward track tile limit)`);
  } else {
    player.tilesPlacedThisTurn += 1;
    debug.log(`[buildTrack] Tiles placed this turn: ${player.tilesPlacedThisTurn}`);
  }

  // Town Disk 管理: Town ヘクスに Town タイル以外を配置した場合
  // - townDisksRemaining を減らす
  // - townDiskPlaced を更新
  let townDiskUsed = false;
  let usedTownId: string | undefined;

  if (hex.town && !tileDef?.isTown && !isNewCityTile) {
    const townId = hex.town.id;
    townDiskUsed = true;
    usedTownId = townId;

    // Town Disk 残数を減らす
    if (G.townDisksRemaining !== undefined && G.townDisksRemaining > 0) {
      G.townDisksRemaining -= 1;
      debug.log(`[buildTrack] Town Disk used. Remaining: ${G.townDisksRemaining}`);
    }

    // この Town に配置済みとしてマーク
    if (!G.townDiskPlaced) {
      G.townDiskPlaced = {};
    }
    G.townDiskPlaced[townId] = true;
    debug.log(`[buildTrack] Town ${townId} marked as having town disk placed`);
  }

  // 7. 履歴に追加（Cancel操作用）
  if (!player.buildTrackHistory) {
    player.buildTrackHistory = [];
  }

  const historyEntry: BuildTrackHistoryEntry = {
    hexCoordinate: args.hexCoordinate,
    tileType: args.tileType,
    rotation: args.rotation,
    placementType: args.placementType,
    cost,
    previousTile,
    previousTownState,
    isNewCityTile,
    // 置き換え時に返却したタイルの種別を記録（ストック復元用）
    returnedTileType: args.placementType === 'replace' && previousTile ? previousTile.type : undefined,
    // Town Disk 使用情報（Cancel時のリセット用）
    townDiskUsed,
    townId: usedTownId,
  };

  player.buildTrackHistory.push(historyEntry);
  debug.log(`[buildTrack] History entry added, total entries: ${player.buildTrackHistory.length}`);

  // 8. 建設コスト情報を更新（UI表示用）
  updateBuildCostInfo(G, ctx.currentPlayer);
}

/**
 * Connections計算（tiles.ts の定義を使用）
 *
 * @param tileType - タイル種別（STRAIGHT, GENTLE_CURVE など）
 * @param rotation - 回転角度（60°単位、0-5）
 * @returns 接続方向のペア配列 [[Direction, Direction], ...]
 *
 * 修正履歴:
 * - 2026-01-06: Issue #2-1 修正 - tiles.ts の定義を使用するように変更
 */
function calculateConnectionsFromTileType(
  tileType: string,
  rotation: 0 | 1 | 2 | 3 | 4 | 5
): number[][] {
  const tileDef = getTileDefinition(tileType);

  if (!tileDef) {
    console.error(`[calculateConnections] Unknown tile type: ${tileType}`);
    return [];
  }

  // tiles.ts の connections は [[0, 3]] 形式（Edgeペア）
  // rotation を適用してペア情報を保持
  return tileDef.connections.map(pair =>
    pair.map(edge => (edge + rotation) % 6)
  );
}

/**
 * タイルID生成
 * @returns タイルID
 */
function generateTileId(): string {
  return `tile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
